import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { buildPlanTasks } from "../../utils/planTasks";
import type { PlanTask } from "../../utils/planTasks";
import { useSessionPersistence } from "../../hooks/useSessionPersistence";
import { unifiedContactService } from "../../services/unifiedContactService";
import {
  assetAggregationService,
  type UnifiedAsset,
} from "../../services/assetAggregationService";
import { isFullyAssigned } from "../../utils/distributionUtils";
// Filled completed-state icon used on finished tasks
const CheckCircle = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <circle cx="10" cy="10" r="10" className="fill-green-700" />
    <path
      d="M7.5 10.5l2 2 3-4"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

// Task icons mirrored from Frame140 so both screens use the same visual language
const BankIcon = ({ className }: { className?: string }) => (
  <svg className={className ?? ""} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2 2 7v2h20V7l-10-5Z" />
    <path d="M4 11h16v9H4v-9Z" />
    <path d="M10 13h4v5h-4v-5Z" fill="#fff" />
  </svg>
);

const HeartIcon = ({ className }: { className?: string }) => (
  <svg className={className ?? ""} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 21s-6.716-4.686-9.646-9.071C-.205 7.4 1.967 3 6.146 3A5.407 5.407 0 0 1 12 6.326 5.407 5.407 0 0 1 17.854 3c4.179 0 6.351 4.4 3.793 8.929C18.716 16.314 12 21 12 21Z" />
  </svg>
);

const CheckCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className ?? ""} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z" />
    <path
      d="m10 14 5-5-1.414-1.414L10 11.172 8.414 9.586 7 11l3 3Z"
      fill="#fff"
    />
  </svg>
);

// Maps the task icon string from planTasks to the matching SVG component
const renderLeftIcon = (type: string, className?: string) => {
  switch (type) {
    case "bank":
      return <BankIcon className={className} />;
    case "heart":
      return <HeartIcon className={`${className ?? ""} text-red-600`} />;
    case "check":
      return <CheckCircleIcon className={`${className ?? ""} text-green-700`} />;
    default:
      return null;
  }
};

// Summary bullet lines shown inside expanded task cards
type TaskDetailMap = Record<string, string[]>;

// From Frame247 — Asset Distribution Summary (types + category helper)
interface BeneficiaryInfo {
  contactId: string;
  name: string;
  percentage?: number;
}

interface AssetDistribution {
  id: string;
  assetId: string;
  assetName: string;
  assetCategory: string;
  primaryBeneficiaries: BeneficiaryInfo[];
  contingentBeneficiaries: BeneficiaryInfo[];
  instructions?: string;
  notes?: string;
  documentUrl?: string;
  documentName?: string;
}

const mapCategoryToKey = (displayCat: string): string => {
  const lower = (displayCat || "").toLowerCase();
  if (lower.includes("financial")) return "financial";
  if (lower.includes("real estate") || lower.includes("property"))
    return "real_estate";
  if (lower.includes("personal")) return "personal";
  if (lower.includes("pet")) return "pets";
  if (lower.includes("digital")) return "digital";
  if (lower.includes("business")) return "business";
  return "personal";
};

/** True if any nested string field has non-whitespace text. Skips `id` keys. */
function hasAnyWrittenInput(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      if (hasAnyWrittenInput(value[i])) return true;
    }
    return false;
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    for (const key in o) {
      if (key === "id") continue;
      if (hasAnyWrittenInput(o[key])) return true;
    }
    return false;
  }
  return false;
}

type LegalDocRow = { type?: string };
type ExecutorRow = { firstName?: string; lastName?: string };
type MemoryCollectionRow = { memories?: string; files?: unknown[] };
type PropertyRow = { currentValue?: number; mortgageBalance?: number };
type FinancialAssetRow = { value?: number };
type PersonalItemRow = { category?: string; estimatedValue?: number };
type DebtRow = { amount?: number };
type ExpenseRow = { frequency?: string; amount?: number };

type DistributionRecord = {
  assetId: string;
  assetCategory?: string;
  primaryBeneficiaries?: Array<{ beneficiaryId: string; percentage?: number }>;
  primaryBeneficiaryId?: string;
  primaryPercentage?: number;
  contingentBeneficiaries?: Array<{ beneficiaryId: string; percentage?: number }>;
  contingentBeneficiaryId?: string;
  instructions?: string;
  notes?: string;
};


// Single task card with navigation and expandable summary details
const TaskRow = ({ task, expandedTasks, taskDetails, navigate, toggleTask }: { task: PlanTask; expandedTasks: Set<string>; taskDetails: TaskDetailMap; navigate: (path: string) => void; toggleTask: (id: string) => void }) => {
  const isExpanded = expandedTasks.has(task.id);
  const details = taskDetails[task.id] ?? [];
  const isCompleted = task.status === "completed";

  // Completed tasks go to summary, unfinished tasks go to their working route
  const navigateToTask = () => {
    const to = isCompleted ? task.summaryRoute : task.route;
    navigate(to);
  };

  return (
    <div className="w-full min-h-[85px] max-w-[830px] rounded-lg border border-stone-200 bg-white overflow-hidden transition shadow-sm hover:shadow-md">
      <div
        role="button"
        tabIndex={0}
        onClick={navigateToTask}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigateToTask();
          }
        }}
        className="w-full px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 text-left hover:bg-stone-50 transition-colors cursor-pointer"
      >
        {/* Left task icon mirrors Frame140 task cards */}
        <div className="flex-shrink-0">
          {renderLeftIcon(task.icon, "w-5 h-5 text-cyan-700")}
        </div>

        {/* Title block */}
        <div className="min-w-0 flex-1">
          <span className="font-sans text-base sm:text-lg font-medium text-stone-800 block whitespace-normal break-words leading-snug">
            {task.title}
          </span>
        </div>

        {/* Status badge */}
        <span
          className={[
            "inline-flex w-fit rounded-full px-2 py-1 text-xs font-medium",
            isCompleted
              ? "bg-green-100 text-green-800"
              : task.status === "next"
              ? "bg-blue-100 text-blue-800"
              : "bg-stone-100 text-stone-700",
          ].join(" ")}
        >
          {isCompleted
            ? "Completed"
            : task.status === "next"
            ? "Next"
            : "Incomplete"}
        </span>

        {/* Secondary completed indicator retained from original component */}
        {isCompleted && <CheckCircle className="w-5 h-5 flex-shrink-0" />}

        {/* Toggle only expands/collapses; it does not navigate */}
        <button
          type="button"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleTask(task.id);
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            e.stopPropagation();

            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleTask(task.id);
            }
          }}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Collapse details" : "Expand details"}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-stone-300 bg-white text-stone-500 transition-colors hover:border-stone-500 hover:text-stone-800"
        >
          <svg
            className={`w-5 h-5 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Expanded task summary pulled from persisted session data */}
      {isExpanded && (
        <div className="px-6 pb-5 pt-3 border-t border-stone-100 bg-stone-50">
          {details.length > 0 ? (
            <ul className="space-y-2 mb-3">
              {details.map((line, idx) => (
                <li
                  key={idx}
                  className="text-sm text-stone-700 flex items-start gap-2"
                >
                  <span className="text-cyan-600 mt-0.5">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-stone-500 mb-3">
              No summary details available yet.
            </p>
          )}

          {/* Summary link is only shown when the task has a summary route */}
          {task.summaryRoute && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(task.summaryRoute);
              }}
              className="text-sm text-cyan-600 hover:text-cyan-700 font-medium hover:underline"
            >
              View Full Summary →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Renders one category section and its task cards
const Section = ({
  title,
  tasks,
  expandedTasks,
  taskDetails,
  navigate,
  toggleTask,
}: {
  title: string;
  tasks: PlanTask[];
  expandedTasks: Set<string>;
  taskDetails: TaskDetailMap;
  navigate: (path: string) => void;
  toggleTask: (id: string) => void;
}) => (
  <section className="mt-8">
    <h2 className="font-sans text-lg font-semibold text-stone-800 mb-3">
      {title}
    </h2>

    {tasks.length === 0 ? (
      <p className="text-sm text-stone-500 max-w-[830px]">
        No tasks in this section.
      </p>
    ) : (
      <div className="space-y-3">
        {tasks.map((t) => (
          <TaskRow key={t.id} task={t} expandedTasks={expandedTasks} taskDetails={taskDetails} navigate={navigate} toggleTask={toggleTask} />
        ))}
      </div>
    )}
  </section>
);


const Frame141: React.FC = () => {
  const navigate = useNavigate();
  const { session, isTaskComplete } = useSessionPersistence();

  // Tracks which task cards are expanded
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  // Stores the computed summary details for each task id
  const [taskDetails, setTaskDetails] = useState<TaskDetailMap>({});

  // Completion flags mirror Frame140 so task status stays consistent between both screens
  const memoryCollectionsComplete =
    Boolean(session.taskProgress?.legacy?.collectionsComplete) ||
    Boolean(session.memoryCollectionsComplete);

  const propertiesTaskComplete = Boolean(isTaskComplete("properties"));

  const inventoryCompleted =
    Boolean(session.taskProgress?.inventory?.completed) ||
    Boolean(isTaskComplete("inventory"));

  const funeralCompleted =
    Boolean(session.taskProgress?.funeral?.completed) ||
    Boolean(isTaskComplete("funeral"));

  const legalEssentialsCompleted =
    Boolean(session.taskProgress?.legal_essentials?.completed) ||
    Boolean(isTaskComplete("legal_essentials"));

  // Builds the same task list structure used on the main plan page
  const { mainTasks, additionalTasks } = buildPlanTasks({
    isTaskComplete,
    memoryCollectionsComplete,
    inventoryCompleted,
    propertiesTaskComplete,
    funeralCompleted,
    legalEssentialsCompleted,
  });

  // Flat list used for grouping and rendering
  const allTasks = React.useMemo(
    () => [...mainTasks, ...additionalTasks],
    [mainTasks, additionalTasks]
  );

  // Groups tasks into the three summary sections shown on this page
  const grouped = React.useMemo(
    () =>
      ({
        Personal: allTasks.filter((t) => t.category === "Personal"),
        Legal: allTasks.filter((t) => t.category === "Legal"),
        Financial: allTasks.filter((t) => t.category === "Financial"),
      } as const),
    [allTasks]
  );

  // Builds the expandable summary bullets for each task from persisted session data
  useEffect(() => {
    const build = async () => {
      const loadWithFallback = <T,>(key: string, defaultValue: T): T => {
        try {
          const backup = localStorage.getItem(key);
          if (backup) return JSON.parse(backup) as T;
        } catch {
          // ignore malformed localStorage backup
        }
        return defaultValue;
      };

      const details: TaskDetailMap = {};

      // Legal Essentials
      const executorsFromNewFlow =
        (session.taskProgress?.executors?.answers?.selectedExecutors as ExecutorRow[] | undefined) || [];
      const executorsFromLegacy =
        (session.taskProgress?.legal_essentials?.answers?.executors as ExecutorRow[] | undefined) || [];
      const executors = executorsFromNewFlow.length > 0 ? executorsFromNewFlow : executorsFromLegacy;
      const legalDocs =
        (session.taskProgress?.legal_documents?.answers?.documents as LegalDocRow[] | undefined) || [];
      const legalDocTypes = legalDocs.reduce<Record<string, number>>((acc, doc) => {
        const t = doc.type ?? "other";
        acc[t] = (acc[t] || 0) + 1;
        return acc;
      }, {});
      const legalDocLabels: Record<string, string> = {
        will: "Will",
        trust: "Living Trust",
        healthcare_power_of_attorney: "Healthcare POA",
        healthcare_directive: "Healthcare Directive",
        other: "Other",
      };
      const legalDocSummary = Object.entries(legalDocTypes).map(
        ([type, count]) => `${count} ${legalDocLabels[type] || type}`
      );
      details["legal_essentials"] = [
        executors.length > 0
          ? `Executors: ${executors
              .map((e) => `${e.firstName ?? ""} ${e.lastName ?? ""}`)
              .join(", ")}`
          : "No executors selected",
        legalDocs.length > 0
          ? `Documents: ${legalDocSummary.join(", ")}`
          : "No legal documents uploaded",
      ];

      // About / Setup Your Plan
      const aboutData = session.taskProgress?.about?.answers || {};
      let contactsCount = 0;
      try {
        const contacts = await unifiedContactService.getAllContacts();
        contactsCount = contacts.length;
      } catch {
        // ignore contact load errors
      }
      details["about"] = [
        `Name: ${session.name || "Not provided"}`,
        `Email: ${session.email || "Not provided"}`,
        `Phone: ${aboutData.phone || session.phone || "Not provided"}`,
        `Contacts imported: ${contactsCount}`,
      ];

      // Legacy / Memories
      const memoryCollections =
        (session.taskProgress?.memoryCollections?.answers?.memoryCollections as MemoryCollectionRow[] | undefined) || [];
      const peopleWithMemories = memoryCollections.filter(
        (c) =>
          (c.memories && c.memories.trim().length > 0) ||
          (c.files && Array.isArray(c.files) && c.files.length > 0)
      );
      const totalFiles = memoryCollections.reduce(
        (sum: number, c: MemoryCollectionRow) => sum + ((Array.isArray(c.files) ? c.files.length : 0) || 0),
        0
      );
      details["legacy"] = [
        `${peopleWithMemories.length} people with memories`,
        `${totalFiles} files uploaded`,
        peopleWithMemories.length > 0
          ? "Memory collections created"
          : "No memories added yet",
      ];
      details["memoryCollections"] = details["legacy"];

      // Inventory of Assets
      const properties =
        (session.taskProgress?.properties?.answers?.properties as PropertyRow[] | undefined) ||
        loadWithFallback<PropertyRow[]>("properties-backup", []);
      const financialAnswers = session.taskProgress?.financial_assets?.answers || {};
      const cashAccounts = (financialAnswers?.cashAccounts as FinancialAssetRow[] | undefined) || loadWithFallback<FinancialAssetRow[]>("cash-accounts-backup", []);
      const investmentAccounts = (financialAnswers?.investmentAccounts as FinancialAssetRow[] | undefined) || loadWithFallback<FinancialAssetRow[]>("investment-accounts-backup", []);
      const retirementAccounts = (financialAnswers?.retirementAccounts as FinancialAssetRow[] | undefined) || loadWithFallback<FinancialAssetRow[]>("retirement-accounts-backup", []);
      const otherFinancialAssets = (financialAnswers?.otherFinancialAssets as FinancialAssetRow[] | undefined) || loadWithFallback<FinancialAssetRow[]>("other-financial-assets-backup", []);
      const allFinancialAssets = [...cashAccounts, ...investmentAccounts, ...retirementAccounts, ...otherFinancialAssets];
      const taxDocs =
        session.taskProgress?.tax_documents?.answers?.documents ||
        loadWithFallback<unknown[]>("tax-documents-backup", []);

      const totalPropertyValue = properties.reduce(
        (sum: number, p: PropertyRow) => sum + (Number(p.currentValue) || 0),
        0
      );
      const totalMortgageBalance = properties.reduce(
        (sum: number, p: PropertyRow) => sum + (Number(p.mortgageBalance) || 0),
        0
      );
      const equity = totalPropertyValue - totalMortgageBalance;
      const totalFinancialValue = allFinancialAssets.reduce(
        (sum: number, a: FinancialAssetRow) => sum + (Number(a.value) || 0),
        0
      );
      const fmt = (n: number) =>
        n.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        });

      details["inventory"] = [
        `${properties.length} ${
          properties.length === 1 ? "property" : "properties"
        } – ${fmt(equity)} equity`,
        `${allFinancialAssets.length} financial assets – ${fmt(
          totalFinancialValue
        )}`,
        `${taxDocs.length} tax documents`,
      ];
      details["properties"] = details["inventory"];

      // Funeral Planning
      const funeralPrefs =
        (session.taskProgress?.funeral?.answers?.funeralPreferences as Record<string, unknown> | undefined) ||
        loadWithFallback<Record<string, unknown> | null>("funeral-preferences-backup", null);
      const memorialDetails =
        (session.taskProgress?.funeral?.answers?.memorialDetails as Record<string, unknown> | undefined) ||
        loadWithFallback<Record<string, unknown> | null>("memorial-details-backup", null);
      const funeralFinalInstructions =
        (session.taskProgress?.funeral?.answers?.finalInstructions as Record<string, unknown> | undefined) ||
        loadWithFallback<Record<string, unknown> | null>("final-instructions-backup", null);


      // Destructure the funeral planning data
      const funeralPreferences = funeralPrefs || {};
      const memDetails = memorialDetails || {};
      const funeralInstructions = funeralFinalInstructions || {};

      // Check if any meaningful data is present in the funeral planning section
      const funeralHasMeaningfulData =
        hasAnyWrittenInput(funeralPreferences) ||
        hasAnyWrittenInput(memDetails) ||
        hasAnyWrittenInput(funeralInstructions) ||
        memDetails.reception === true;

      const funeralLines: string[] = [];

      if (!funeralHasMeaningfulData) {
        funeralLines.push("No memorial details planned yet");
      } else {
        const serviceType = funeralPreferences.serviceType;
        if (serviceType && serviceType !== "" && serviceType !== "none") { // If a service type is selected, add it to the lines
          const svc: Record<string, string> = { // Use record to get the service type label, preventing undefined or null values
            traditional: "Traditional funeral service",
            memorial: "Memorial service",
            celebration: "Celebration of life",
          };
          const svcLabel = svc[serviceType as string] || String(serviceType);
          if (svcLabel.trim()) funeralLines.push(svcLabel.trim());
        }

        let line2 = "";
        if (funeralPreferences.customLocation && String(funeralPreferences.customLocation).trim()) {
          line2 = String(funeralPreferences.customLocation).trim();
        } else if (funeralPreferences.location) {
          const loc: Record<string, string> = { // If a location is selected, add it to the lines
            funeral_home: "Funeral home",
            religious: "Religious institution",
            home: "Family home",
            outdoor: "Outdoor location",
            other: "Other location",
          };
          line2 = loc[funeralPreferences.location as string] || ""; // use record to get the location label, preventing undefined or null values
        }
        if (!line2 && memDetails.reception === true) line2 = "Reception planned";
        if (!line2 && memDetails.receptionDetails && String(memDetails.receptionDetails).trim()) {
          line2 = "Reception planned";
        }
        if (line2) funeralLines.push(line2);
        funeralLines.push("Memorial details planned");
      }

      details["funeral"] = funeralLines.slice(0, 3); // Slice the array to only include the first 3 lines

      // Personal Belongings
      const personalItems =
        (session.taskProgress?.personal?.answers?.personalItems as PersonalItemRow[] | undefined) ||
        loadWithFallback<PersonalItemRow[]>("personal-items-backup", []);
      const totalItemValue = personalItems.reduce(
        (sum: number, i: PersonalItemRow) => sum + (Number(i.estimatedValue) || 0),
        0
      );
      const categoryBreakdown = personalItems.reduce<Record<string, number>>((acc, item) => {
        const cat = item.category || "custom";
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {});
      const categoryLabels: Record<string, string> = {
        documents: "Documents",
        physical_assets: "Physical Assets",
        collectibles: "Collectibles",
        electronics: "Electronics",
        digital_assets: "Digital Assets",
        custom: "Other",
      };
      details["personal"] = [
        `${personalItems.length} items – ${fmt(totalItemValue)}`,
        ...Object.entries(categoryBreakdown).map(
          ([cat, count]) => `${count} ${categoryLabels[cat] || cat}`
        ),
      ];

      // Debts & Final Expenses
      const debts = (session.taskProgress?.debts?.answers?.debts as DebtRow[] | undefined) || [];
      const expenses =
        (session.taskProgress?.monthly_expenses?.answers?.expenses as ExpenseRow[] | undefined) || [];
      const totalDebt = debts.reduce(
        (sum: number, d: DebtRow) => sum + (Number(d.amount) || 0),
        0
      );
      const calcMonthly = (exp: ExpenseRow) => {
        const amt = Number(exp.amount) || 0;
        switch (exp.frequency) {
          case "quarterly":
            return amt / 3;
          case "annual":
            return amt / 12;
          default:
            return amt;
        }
      };
      const totalMonthlyExpenses = expenses.reduce(
        (sum: number, e: ExpenseRow) => sum + calcMonthly(e),
        0
      );
      details["debts"] = [
        `${debts.length} outstanding debts – ${fmt(totalDebt)}`,
        `${expenses.length} monthly expenses – ${fmt(totalMonthlyExpenses)}/mo`,
      ];

      // Assign and Approve Asset Distribution (Asset Distribution Summary — Frame 247)
      // 1. Get raw saved distributions (Record<assetId, DistributionRecord>)
      const rawDistributions =
        session.taskProgress?.assetDistribution?.answers?.distributions || {};

      // 2. Fetch dependencies to hydrate names
      // Assets
      const assets = assetAggregationService.getAllAssets(session);

      // Contacts (async)
      let contacts: { id: string; firstName: string; lastName: string }[] = [];
      try {
        contacts = await unifiedContactService.getAllContacts();
        console.log("[Frame247] Loaded contacts:", contacts.length);
      } catch (e) {
        console.error("Failed to load contacts for summary", e);
      }

      // 3. Map and Hydrate manually-assigned distributions
      // Handle both Array (legacy) and Record (new) formats just in case
      const distArray: DistributionRecord[] = Array.isArray(rawDistributions)
        ? (rawDistributions as DistributionRecord[])
        : Object.values(rawDistributions as Record<string, DistributionRecord>);

      // Track which asset IDs have manual assignments
      const manuallyAssignedIds = new Set<string>();

      const hydrated = distArray
        .map((d) => {
          const asset = assets.find((a: UnifiedAsset) => a.id === d.assetId);

          if (!asset) return null;

          manuallyAssignedIds.add(d.assetId);

          // Safe Defaults
          const assetName = asset.name || "Unknown Asset";
          const assetCat =
            d.assetCategory || asset.category || asset.sourceType || "personal";

          // Hydrate primary beneficiaries — support both new and legacy formats
          let primaryBeneficiaries: BeneficiaryInfo[] = [];
          if (
            Array.isArray(d.primaryBeneficiaries) &&
            d.primaryBeneficiaries.length > 0
          ) {
            // New format: array of { beneficiaryId, percentage }
            primaryBeneficiaries = d.primaryBeneficiaries.map((pb) => {
              const contact = contacts.find((c) => c.id === pb.beneficiaryId);
              return {
                contactId: pb.beneficiaryId,
                name: contact
                  ? `${contact.firstName} ${contact.lastName}`
                  : "Unknown Beneficiary",
                percentage:
                  typeof pb.percentage === "number" ? pb.percentage : 100,
              };
            });
          } else if (d.primaryBeneficiaryId) {
            // Legacy format: single beneficiary fields
            const contact = contacts.find(
              (c) => c.id === d.primaryBeneficiaryId
            );
            primaryBeneficiaries = [
              {
                contactId: d.primaryBeneficiaryId,
                name: contact
                  ? `${contact.firstName} ${contact.lastName}`
                  : "Unknown Beneficiary",
                percentage:
                  typeof d.primaryPercentage === "number"
                    ? d.primaryPercentage
                    : 100,
              },
            ];
          }

          // Hydrate contingent beneficiaries — support both new and legacy formats
          let contingentBeneficiaries: BeneficiaryInfo[] = [];
          if (
            Array.isArray(d.contingentBeneficiaries) &&
            d.contingentBeneficiaries.length > 0
          ) {
            contingentBeneficiaries = d.contingentBeneficiaries.map(
              (cb) => {
                const contact = contacts.find(
                  (c) => c.id === cb.beneficiaryId
                );
                return {
                  contactId: cb.beneficiaryId,
                  name: contact
                    ? `${contact.firstName} ${contact.lastName}`
                    : "Unknown Beneficiary",
                  percentage:
                    typeof cb.percentage === "number" ? cb.percentage : 100,
                };
              }
            );
          } else if (d.contingentBeneficiaryId) {
            const contact = contacts.find(
              (c) => c.id === d.contingentBeneficiaryId
            );
            contingentBeneficiaries = [
              {
                contactId: d.contingentBeneficiaryId,
                name: contact
                  ? `${contact.firstName} ${contact.lastName}`
                  : "Unknown Beneficiary",
                percentage: 100,
              },
            ];
          }

          const item: AssetDistribution = {
            id: d.assetId || "unknown-id",
            assetId: d.assetId || "unknown-id",
            assetName: assetName,
            assetCategory: mapCategoryToKey(assetCat),
            primaryBeneficiaries,
            contingentBeneficiaries,
            instructions: d.instructions,
            notes: d.notes,
          };
          return item;
        })
        .filter((item): item is AssetDistribution => item !== null);

      // 4. Include pre-assigned assets (existingBeneficiaries from upstream)
      //    that were NOT manually overridden in Frame246
      const preAssigned: AssetDistribution[] = assets
        .filter(
          (asset) =>
            !manuallyAssignedIds.has(asset.id) &&
            asset.existingBeneficiaries &&
            asset.existingBeneficiaries.length > 0
        )
        .map((asset) => ({
          id: asset.id,
          assetId: asset.id,
          assetName: asset.name || "Unknown Asset",
          assetCategory: mapCategoryToKey(
            asset.category || asset.sourceType || "personal"
          ),
          primaryBeneficiaries: asset.existingBeneficiaries!.map((eb) => ({
            contactId: eb.contactId || "",
            name: eb.name || "Unknown Beneficiary",
            percentage: eb.percentage || 100,
          })),
          contingentBeneficiaries: [],
          instructions: undefined,
          notes: undefined,
        }));

      const distributions: AssetDistribution[] = [...hydrated, ...preAssigned];

      // Compute flaggedAssets - assets needing attention (no beneficiaries OR incomplete %)
      const flaggedAssets: {
        id: string;
        name: string;
        category: string;
        reason: string;
      }[] = [];
      const allAssets = assetAggregationService.getAllAssets(session);

      for (const asset of allAssets) {
        const dist = distributions.find((d) => d.assetId === asset.id);
        if (
          !dist ||
          !dist.primaryBeneficiaries ||
          dist.primaryBeneficiaries.length === 0
        ) {
          flaggedAssets.push({
            id: asset.id,
            name: asset.name || "Unknown Asset",
            category: asset.sourceType || "personal",
            reason: "No beneficiaries assigned",
          });
        } else {
          const total = dist.primaryBeneficiaries.reduce(
            (sum, b) => sum + (Number(b.percentage) || 0),
            0
          );
          if (total !== 100) {
            flaggedAssets.push({
              id: asset.id,
              name: asset.name || "Unknown Asset",
              category: asset.sourceType || "personal",
              reason: `${total}% assigned — 100% required`,
            });
          }
        }
      }

      // Only include fully assigned assets
      const assignedDistributions = distributions.filter((d) =>
        isFullyAssigned(d)
      );

      const beneficiaryIdSeen: Record<string, true> = {};
      for (const dist of assignedDistributions) {
        for (const pb of dist.primaryBeneficiaries) {
          if (pb.contactId) beneficiaryIdSeen[pb.contactId] = true;
        }
      }
      const beneficiaryCount = Object.keys(beneficiaryIdSeen).length;

      const totalAssetsCount = allAssets.length;
      let distributionStatusLine: string;
      if (totalAssetsCount === 0) {
        distributionStatusLine = "No distribution plan yet";
      } else if (flaggedAssets.length === 0) {
        distributionStatusLine = "Distribution plan created";
      } else {
        distributionStatusLine = "Some assets unassigned";
      }

      details["distribution"] = [
        `${totalAssetsCount} ${
          totalAssetsCount === 1 ? "asset" : "assets"
        }`,
        `${beneficiaryCount} ${
          beneficiaryCount === 1 ? "beneficiary" : "beneficiaries"
        }`,
        distributionStatusLine,
      ];

      // Pets & Dependents
      const petsData =
        session.taskProgress?.pets?.answers?.petsAndDependants ||
        loadWithFallback("pets-dependants-backup", null);
      const petsCount = petsData?.pets?.length || 0;
      const dependentsCount = petsData?.dependants?.length || 0;
      const financialArrangements =
        session.taskProgress?.pets?.answers?.financialArrangements;
      const finalInstructions =
        session.taskProgress?.pets?.answers?.finalInstructions;
      const petsList = petsData?.pets || [];
      const dependantsList = petsData?.dependants || [];

      let carePlanDocumented = false;
      for (let i = 0; i < petsList.length && !carePlanDocumented; i++) {
        if (hasAnyWrittenInput(petsList[i])) carePlanDocumented = true;
      }
      for (let i = 0; i < dependantsList.length && !carePlanDocumented; i++) {
        if (hasAnyWrittenInput(dependantsList[i])) carePlanDocumented = true;
      }
      if (!carePlanDocumented && hasAnyWrittenInput(financialArrangements)) {
        carePlanDocumented = true;
      }
      if (!carePlanDocumented && hasAnyWrittenInput(finalInstructions)) {
        carePlanDocumented = true;
      }

      details["pets"] = [
        `${petsCount} ${petsCount === 1 ? "pet" : "pets"}`,
        `${dependentsCount} ${
          dependentsCount === 1 ? "dependent" : "dependents"
        }`,
        carePlanDocumented ? "Care plan documented" : "No care plan yet",
      ];

      setTaskDetails(details);
    };

    build();
  }, [session, isTaskComplete]);

  // Expands or collapses a task details panel
  const toggleTask = (id: string) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px]">
          <ResponsiveHeader title="My Plan Summary" />

          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            Your complete estate plan at a glance. View all your directives and
            records in one place.
          </p>

          <Section title="Personal Wishes" tasks={grouped.Personal} expandedTasks={expandedTasks} taskDetails={taskDetails} navigate={navigate} toggleTask={toggleTask} />
          <Section title="Financial Guidance" tasks={grouped.Financial} expandedTasks={expandedTasks} taskDetails={taskDetails} navigate={navigate} toggleTask={toggleTask} />
          <Section title="Legal Directives" tasks={grouped.Legal} expandedTasks={expandedTasks} taskDetails={taskDetails} navigate={navigate} toggleTask={toggleTask} />
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default Frame141;